import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { type AxiosError } from 'axios';

import { useAppDispatch } from '@/Application/Hooks/Redux';
import { URL_ROUTES } from '@/constants';
import { useMapOrderToConfirmation } from '@/modules/perseo-billing/hooks/order-confirmation/use-map-order-confirmation';
import { useClearPaymentMethod } from '@/modules/perseo-billing/hooks/use-clear-payment-method';
import { usePaymentError } from '@/modules/perseo-billing/hooks/use-payment-error';
import { buildPurchaseSearchParams } from '@/modules/perseo-billing/hooks/utils';
import { type IConfirmOrderIntentBody } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { postConfirmOrderIntent } from '@/modules/perseo-billing/services/api/billing/index';
import { billingMutationKeys } from '@/modules/perseo-billing/services/api/billing/mutations/billing-mutation-factory';
import {
  setIsPaymentComplete,
  setIsProcessingPayment,
  setPaymentSuccess
} from '@/modules/perseo-billing/store/slices/billing/index';
import { type IErrorResponse } from '@/modules/perseo-billing/utils/error-handler/index';

export const useMutateConfirmOrderIntent = (
  orderId: string,
  customConfig?: Record<string, unknown>
) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { clearPayment } = useClearPaymentMethod();
  const { handlePaymentError } = usePaymentError();
  const { transformData } = useMapOrderToConfirmation();

  const confirmOrderIntent = useMutation(
    (params: IConfirmOrderIntentBody) => postConfirmOrderIntent(params),
    {
      mutationKey: [billingMutationKeys.confirmOrderIntent, orderId],
      onSuccess(data) {
        const orderConfirmation = transformData(data);
        dispatch(setIsProcessingPayment(false));
        dispatch(setIsPaymentComplete(true));
        dispatch(setPaymentSuccess(orderConfirmation));

        const urlSearchParams = buildPurchaseSearchParams(
          orderConfirmation?.id
        );
        const paramsString = urlSearchParams.toString();
        const orderProcessingUrl = `${URL_ROUTES.BILLING_ORDER_PROCESSING_SUCCESS_PATH}?${paramsString}`;
        navigate(orderProcessingUrl, { replace: true });

        clearPayment();
      },
      onError(error: AxiosError<IErrorResponse>) {
        console.error('Error confirming order intent:', error);
        handlePaymentError(error?.response?.data);
        dispatch(setIsProcessingPayment(false));
      },
      onMutate() {
        dispatch(setIsProcessingPayment(true));
      },
      ...customConfig
    }
  );

  return confirmOrderIntent;
};
